import React, { useCallback, useEffect, useState } from 'react'
import DataGrid, {
	Column,
	Editing,
	Lookup,
	Scrolling,
	Paging,
	FilterRow,
	HeaderFilter,
} from 'devextreme-react/data-grid'
import axios from 'axios'
import notify from '../../utils/notify'
import { insertData, editData, deleteData, lookupStore, filterStore, fixDataType } from '../../utils/model-data'

type ModelGridPopupArgs = {
	folder: string
	model: string
	title?: string
}

type ModelGridPopupProps = { args: ModelGridPopupArgs }

type FieldMeta = { dataField: string; dataType?: string; caption?: string }

const ModelGridPopup: React.FC<ModelGridPopupProps> = ({ args }) => {
	const { folder, model, title } = args

	const [rows, setRows] = useState<any[]>([])
	const [fields, setFields] = useState<FieldMeta[]>([])
	const [lookups, setLookups] = useState<Record<string, any>>({})
	const [locks, setLocks] = useState<string[]>([])
	const [keyField, setKeyField] = useState<string>('id')
	const [inserting, setInserting] = useState(false)
	const [updating, setUpdating] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [loading, setLoading] = useState(false)

	const loadMeta = useCallback(async () => {
		try {
			const response = await axios.get(`${API_FIBER_URL}/view/fields?folder=${folder}&model=${model}`)
			const data = response?.data?.data || {}
			setFields(Array.isArray(data.fields) ? data.fields : [])
			setLookups(data.lookups || {})
			setLocks(Array.isArray(data.locks) ? data.locks : [])
			setKeyField(typeof data.key === 'string' ? data.key : 'id')
			setInserting(Boolean(data.inserting))
			setUpdating(Boolean(data.updating))
			setDeleting(Boolean(data.deleting))
		} catch (error: any) {
			notify(error?.response?.data?.message || 'Error loading metadata', 'error')
		}
	}, [folder, model])

	const loadRows = useCallback(async () => {
		setLoading(true)
		try {
			const response = await axios.post(`${API_FIBER_URL}/view/data`, {
				skip: 0,
				take: 5000,
				model,
				folder,
			})
			const data = Array.isArray(response.data?.data) ? response.data.data : []
			setRows(data)
		} catch (error: any) {
			notify(error?.response?.data?.message || 'Error loading data', 'error')
		} finally {
			setLoading(false)
		}
	}, [folder, model])

	useEffect(() => {
		loadMeta()
		loadRows()
	}, [loadMeta, loadRows])

	const onRowInserted = async (e: any) => {
		const { [keyField]: _omitKey, ...values } = e.data || {}
		try {
			await insertData('view', { values, folder, model })
			notify('Added successfully', 'success')
			loadRows()
		} catch (error: any) {
			notify(error || 'Insert failed', 'error')
			loadRows()
		}
	}

	const onRowUpdated = async (e: any) => {
		const key = e.key
		if (key == null) return
		try {
			await editData('view', { key, values: e.data, folder, model })
			notify('Updated successfully', 'success')
			loadRows()
		} catch (error: any) {
			notify(error || 'Update failed', 'error')
			loadRows()
		}
	}

	const onRowRemoved = async (e: any) => {
		const key = e.key
		if (key == null) return
		try {
			await deleteData('view', { key, folder, model })
			notify('Deleted successfully', 'success')
			loadRows()
		} catch (error: any) {
			notify(error || 'Delete failed', 'error')
			loadRows()
		}
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', minWidth: 700 }}>
			{title ? <p style={{ fontSize: 16, fontWeight: 'bold', margin: 0 }}>{title}</p> : null}
			<DataGrid
				dataSource={rows}
				keyExpr={keyField}
				showBorders={true}
				columnAutoWidth={true}
				height={500}
				loadPanel={{ enabled: loading }}
				onRowInserted={onRowInserted}
				onRowUpdated={onRowUpdated}
				onRowRemoved={onRowRemoved}
			>
				<Scrolling mode="standard" />
				<Paging enabled={false} />
				<FilterRow visible={true} applyFilter="auto" />
				<HeaderFilter visible={true} search={{ enabled: true }} />
				<Editing
					mode="row"
					allowAdding={inserting}
					allowUpdating={updating}
					allowDeleting={deleting}
					useIcons={true}
				/>
				{fields.map(field => {
					const lookup = lookups[field.dataField]
					return (
						<Column
							key={field.dataField}
							dataField={field.dataField}
							dataType={fixDataType(field.dataType ?? '', field.dataField)}
							caption={field.caption ?? field.dataField}
							allowEditing={!locks.includes(field.dataField)}
							allowFiltering={!lookup}
							allowHeaderFiltering={Boolean(lookup)}
						>
							{lookup && (
								<HeaderFilter
									allowSelectAll={false}
									search={{ enabled: true, searchExpr: 'text' }}
									dataSource={filterStore(field.dataField, lookup)}
								/>
							)}
							{lookup && <Lookup dataSource={lookupStore(lookup)} valueExpr="value" displayExpr="text" />}
						</Column>
					)
				})}
			</DataGrid>
		</div>
	)
}

export default ModelGridPopup
